import * as React from 'react';
import { RefObject, forwardRef } from 'react';
import { buttonIconMap, variantMap, fontColorMap } from './button.constant';
// --> types
import { ButtonProps, variant, ButtonSizeType, FontColorType } from './button.types';
import { FontWeightType, Spinner, fontWeightMap } from '../..';
import { cn } from '@restworld/utils-common';

// --> main components
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Button = forwardRef(
  (
    props: ButtonProps,
    ref?: ((instance: HTMLButtonElement | null) => void) | RefObject<HTMLButtonElement | null> | null
  ) => {
    const {
      type = 'button',
      title = '',
      variant = 'normal',
      icon,
      suffixIcon,
      onClick: handleClick,
      buttonSize,
      disabled,
      isUnderLined,
      fontColor,
      fullwidth,
      children,
      borderLess,
      loading,
      isRoundedFull,
      twStyle,
      withLabelUnderneathIcon,
      loaderSize = 'sm',
      fitContent,
      fontWeight = 'semibold',
      testId,
      ...rest
    } = props;
    const iconSVGElement = icon ? <span onMouseDown={(e) => e.stopPropagation()}>{buttonIconMap[icon]}</span> : null;
    return (
      <StyledButton
        type={type}
        disabled={disabled}
        buttonSize={buttonSize}
        variant={variant}
        onClick={(e) => handleClick && handleClick(e)}
        suffixIcon={suffixIcon}
        fontColor={fontColor}
        fullwidth={fullwidth}
        borderless={borderLess ? '1' : '0'}
        loading={loading ? '1' : '0'}
        isroundedfull={isRoundedFull ? '1' : '0'}
        twstyle={twStyle}
        $withLabelUnderneathIcon={withLabelUnderneathIcon}
        fitContent={fitContent}
        withIconOnly={!(title || children)}
        fontWeight={fontWeight}
        ref={ref}
        data-cy={testId}
        {...rest}
      >
        {loading ? (
          <Spinner color="primary" size={loaderSize} />
        ) : (
          <>
            <ButtonTitleWrapper id="button-label" $withLabelUnderneathIcon={withLabelUnderneathIcon}>
              {iconSVGElement}
              {title || children ? (
                <ButtonTitle id="button-label" isUnderLined={isUnderLined}>
                  {title || children}
                </ButtonTitle>
              ) : null}
            </ButtonTitleWrapper>
            {suffixIcon ? (
              <span id="button-icon" className="ml-2">
                {buttonIconMap[suffixIcon]}
              </span>
            ) : null}
          </>
        )}
      </StyledButton>
    );
  }
);
export default Button;
// --> styled components
const getVariantDefault = (variant: string) => {
  switch (variant) {
    case 'custom':
      return 'font-semibold';
    case 'ghost':
      return 'p-0.5 h-8 self-center max-w-fit';
    default:
      return 'focus:ring-4 focus:outline-none focus:ring-secondary-300';
  }
};

const StyledButton = ({
  variant,
  suffixIcon,
  fontColor,
  fullwidth,
  borderless,
  disabled,
  loading,
  isroundedfull,
  $withLabelUnderneathIcon,
  fitContent,
  withIconOnly,
  fontWeight,
  twstyle,
  buttonSize,
  className,
  ...props
}: any) => (
  <button
    className={cn(
      'inline-flex items-center max-w-md cursor-pointer relative w-max px-[20px] py-[14px] rounded-lg font-semibold lg:text-sm lg:leading-[15.4px] md:leading-[15.4px] md:text-sm text-base leading-[15.4px]',
      getVariantDefault(variant),
      variantMap[variant],
      suffixIcon ? 'justify-between' : 'justify-center',
      fontColor ? fontColorMap[fontColor] : '',
      fullwidth ? 'w-full max-w-full' : '',
      borderless === '1' ? 'border-0' : '',
      disabled ? 'bg-gray-100 text-gray-400 pointer-events-none !cursor-not-allowed ring-0 border-none' : '',
      loading === '1' ? 'pointer-events-none' : '',
      isroundedfull === '1' ? 'rounded-full p-2' : '',
      $withLabelUnderneathIcon ? 'text-gray-400' : '',
      fitContent ? 'min-w-fit' : '',
      withIconOnly ? 'px-[14px] py-[14px]' : '',
      fontWeight ? fontWeightMap[fontWeight] : '',
      twstyle,
      className
    )}
    {...props}
  />
);
const ButtonTitle = ({ isUnderLined, className, ...props }: any) => (
  <span className={cn('cursor-pointer', isUnderLined ? 'underline underline-offset-4' : '', className)} {...props} />
);
const ButtonTitleWrapper = ({ $withLabelUnderneathIcon, className, ...props }: any) => (
  <span
    className={cn(
      'flex items-center gap-2 cursor-pointer',
      $withLabelUnderneathIcon ? 'flex-col items-center gap-0' : '',
      className
    )}
    {...props}
  />
);
